import {Async, Loaded, Loading} from 'Core/models/Async';
import {StringUnion} from 'Core/models/EmptyString';

export class UnassignedPolicyNumber {
  public static create(policyNumber: StringUnion) {
    return new UnassignedPolicyNumber(policyNumber);
  }

  public readonly key = 'UnassignedPolicyNumber';
  constructor(public readonly value: StringUnion) {}
}

export class UserAssignedPolicyNumber {
  public static create(policyNumber: string) {
    return new UserAssignedPolicyNumber(policyNumber);
  }

  public readonly key = 'UserAssignedPolicyNumber';
  constructor(public readonly value: string) {}
}

export class Reserved {
  public static create(policyNumber: string) {
    return new Reserved(policyNumber);
  }

  public readonly key = 'Reserved';
  constructor(public readonly value: string) {}
}

export class PolicyNumberError {
  public static create(policyNumber: string) {
    return new PolicyNumberError(policyNumber);
  }

  public readonly key = 'PolicyNumberError';
  constructor(public readonly value: string) {}
}

export class PolicyNumberValidating {
  public static create(policyNumber: string) {
    return new PolicyNumberValidating(policyNumber);
  }

  public readonly key = 'PolicyNumberValidating';
  constructor(public readonly value: string) {}
}

export type PolicyNumber =
  | Async<string>
  | UnassignedPolicyNumber
  | UserAssignedPolicyNumber
  | Reserved
  | PolicyNumberError
  | PolicyNumberValidating;

type MatchPolicyNumberOptions<TReturn> = {
  Loading: () => TReturn;
  Loaded: (policyNumber: string) => TReturn;
  Error: () => TReturn;
  Unassigned: (policyNumber: StringUnion) => TReturn;
  UserAssignedPolicyNumber: (policyNumber: string) => TReturn;
  Reserved: (policyNumber: string) => TReturn;
  PolicyNumberError: (policyNumber: string) => TReturn;
  PolicyNumberValidating: (policyNumber: string) => TReturn;
};

export const isLoading = (policyNumber: PolicyNumber): policyNumber is Loading<string> =>
  policyNumber.key === 'Loading';

export const isUserAssignedPolicyNumber = (policyNumber: PolicyNumber): policyNumber is UserAssignedPolicyNumber =>
  policyNumber.key === 'UserAssignedPolicyNumber';

export const isLoaded = (policyNumber: PolicyNumber): policyNumber is Loaded<string> => policyNumber.key === 'Loaded';

export const isReserved = (policyNumber: PolicyNumber): policyNumber is Reserved => policyNumber.key === 'Reserved';

export const isValidating = (policyNumber: PolicyNumber): policyNumber is PolicyNumberValidating =>
  policyNumber.key === 'PolicyNumberValidating';

export const isPolicyNumberError = (policyNumber: PolicyNumber): policyNumber is PolicyNumberError =>
  policyNumber.key === 'PolicyNumberError';

export const matchPolicyNumber = <TReturn>(options: MatchPolicyNumberOptions<TReturn>) => (
  policyNumber: PolicyNumber
) => {
  switch (policyNumber.key) {
    case 'UnassignedPolicyNumber':
      return options.Unassigned(policyNumber.value);
    case 'Loaded':
      return options.Loaded(policyNumber.Data);
    case 'Loading':
      return options.Loading();
    case 'Conflict':
      return options.Error();
    case 'ServerError':
      return options.Error();
    case 'Unauthorized':
      return options.Error();
    case 'UserAssignedPolicyNumber':
      return options.UserAssignedPolicyNumber(policyNumber.value);
    case 'PolicyNumberValidating':
      return options.PolicyNumberValidating(policyNumber.value);
    case 'Reserved':
      return options.Reserved(policyNumber.value);
    case 'PolicyNumberError':
      return options.PolicyNumberError(policyNumber.value);
  }
};
